import apiClient from './client';
import { apiFailure } from './errors';
import type { Entitlements, Invoice, PricesResponse } from '@/types/subscription';

/**
 * Error thrown when a plan change / cancel is blocked because current usage
 * would exceed the target tier's hard limits. Carries the structured impact
 * so the UI can render specific remediation guidance (archive N vaults,
 * remove M members, etc.).
 */
export class DowngradeBlockedError extends Error {
  readonly code = 'DOWNGRADE_BLOCKED';
  readonly impact: DowngradeImpact;
  constructor(message: string, impact: DowngradeImpact) {
    super(message);
    this.name = 'DowngradeBlockedError';
    this.impact = impact;
  }
}

/**
 * Thrown when the plan change can't complete in-app and the user needs to
 * finish it via the Stripe billing portal (3DS / card declined / payment
 * method requires update). UI should offer a direct "Open billing portal"
 * button rather than ask the user to navigate manually.
 *
 * `reason` is a machine-readable discriminator — the UI uses it to pick
 * tailored copy (e.g. "Your card was declined" vs. "3D Secure required")
 * instead of showing one generic portal message for every branch.
 */
export type PortalRequiredReason = '3ds_required' | 'card_declined' | 'incomplete';

export class PortalRequiredError extends Error {
  readonly code = 'PORTAL_REQUIRED';
  readonly reason: PortalRequiredReason;
  constructor(message: string, reason: PortalRequiredReason = 'incomplete') {
    super(message);
    this.name = 'PortalRequiredError';
    this.reason = reason;
  }
}

/**
 * Thrown when an upgrade applied on the Stripe side but the proration
 * invoice still needs to be paid (3DS challenge, declined card, etc.).
 * Carries the hosted invoice URL so the UI can redirect the user straight
 * to the Stripe page that actually lets them complete the payment —
 * the billing portal can't act on an in-flight charge.
 */
export class InvoicePaymentRequiredError extends Error {
  readonly code = 'INVOICE_PAYMENT_REQUIRED';
  readonly invoiceUrl: string;
  constructor(message: string, invoiceUrl: string) {
    super(message);
    this.name = 'InvoicePaymentRequiredError';
    this.invoiceUrl = invoiceUrl;
  }
}

interface ApiResponse<T> {
  status: string;
  data: T;
}

interface EntitlementsResponse {
  success: boolean;
  entitlements?: Entitlements;
  error?: string;
}

interface InvoicesListResponse {
  success: boolean;
  invoices?: Invoice[];
  total: number;
  error?: string;
}

interface InvoiceResponse {
  success: boolean;
  invoice?: Invoice;
  error?: string;
}

interface StripePricesResponse {
  publishableKey?: string;
  tiers?: PricesResponse['tiers'];
  error?: string;
}

interface CheckoutSessionResponse {
  success: boolean;
  sessionId?: string;
  url?: string;
  error?: string;
}

interface PortalSessionResponse {
  success: boolean;
  url?: string;
  error?: string;
}

interface ChangePlanResponse {
  success: boolean;
  error?: string;
  errorCode?: string;
  // When errorCode === 'PORTAL_REQUIRED', a machine-readable discriminator
  // so the UI can render 3DS vs. card-declined copy specifically.
  errorReason?: string;
  // Set when errorCode === 'INVOICE_PAYMENT_REQUIRED' — the Stripe-hosted
  // invoice URL the user must visit to complete payment / 3DS.
  invoiceUrl?: string;
  // Set on successful upgrade — URL of the Stripe billing-portal
  // subscription_update_confirm flow. The client redirects the user there
  // to confirm the upgrade and complete payment atomically.
  redirectUrl?: string;
  // On DOWNGRADE_BLOCKED, backend returns the overage details so the UI
  // can show the user exactly what needs to be remediated.
  impact?: DowngradeImpact;
}

export interface DowngradeOverage {
  limit: 'maxVaults' | 'maxMembers';
  current: number;
  newLimit: number;
}

export interface VaultAddressOverage {
  vaultId: string;
  vaultName: string;
  current: number;
  newLimit: number;
}

export interface RetentionChange {
  historyDays?: { current: number; newLimit: number };
  auditLogDays?: { current: number; newLimit: number };
}

export interface DowngradeImpact {
  overages: DowngradeOverage[];
  vaultAddressOverages: VaultAddressOverage[];
  featuresLost: string[];
  retentionReduced: RetentionChange;
  blocked: boolean;
}

export interface PlanChangePreview {
  direction: 'upgrade' | 'downgrade';
  immediateAmount: number;
  currency: string;
  currentPeriodEnd: string;
  newTier: 'free' | 'starter' | 'pro' | 'business';
  currentTier: 'free' | 'starter' | 'pro' | 'business';
  impact?: DowngradeImpact;
  /** Returned when a coupon code was successfully applied to the preview. */
  couponApplied?: boolean;
}

interface PreviewPlanChangeResponse {
  success: boolean;
  preview?: PlanChangePreview;
  error?: string;
}

interface CancelSubscriptionResponse {
  success: boolean;
  currentPeriodEnd?: string;
  error?: string;
  errorCode?: string;
  impact?: DowngradeImpact;
}

interface ResumeSubscriptionResponse {
  success: boolean;
  error?: string;
}

export interface InvoiceQueryParams {
  page?: number;
  limit?: number;
  status?: string;
}

// Module-scoped cache for Stripe price IDs. See getStripePrices below.
const PRICES_TTL_MS = 10 * 60 * 1000;
let pricesCache: { data: PricesResponse; fetchedAt: number } | null = null;

function buildQueryString(params?: object): string {
  if (!params) return '';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

export const subscriptionApi = {
  getEntitlements: async (orgId: string): Promise<Entitlements> => {
    const response = await apiClient.get<ApiResponse<EntitlementsResponse>>(
      `/organizations/${orgId}/entitlements`
    );
    if (!response.data.data.success || !response.data.data.entitlements) {
      throw apiFailure(response.data.data, 'Failed to get entitlements');
    }
    return response.data.data.entitlements;
  },

  getInvoices: async (
    orgId: string,
    params?: InvoiceQueryParams
  ): Promise<{ invoices: Invoice[]; total: number }> => {
    const qs = buildQueryString(params);
    const response = await apiClient.get<ApiResponse<InvoicesListResponse>>(
      `/organizations/${orgId}/invoices${qs}`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get invoices');
    }
    return {
      invoices: response.data.data.invoices || [],
      total: response.data.data.total || 0,
    };
  },

  getInvoice: async (orgId: string, invoiceId: string): Promise<Invoice> => {
    const response = await apiClient.get<ApiResponse<InvoiceResponse>>(
      `/organizations/${orgId}/invoices/${invoiceId}`
    );
    if (!response.data.data.success || !response.data.data.invoice) {
      throw apiFailure(response.data.data, 'Failed to get invoice');
    }
    return response.data.data.invoice;
  },

  getStripePrices: async (): Promise<PricesResponse> => {
    // Price IDs almost never change at runtime — they're registered in
    // Stripe and only updated when we intentionally refresh tier pricing.
    // Cache at module scope so navigating between Billing and Pricing (or
    // opening PlanChangeModal multiple times) doesn't refetch. 10-minute
    // TTL leaves room to pick up intentional changes without requiring a
    // hard reload.
    const now = Date.now();
    if (pricesCache && now - pricesCache.fetchedAt < PRICES_TTL_MS) {
      return pricesCache.data;
    }
    const response = await apiClient.get<ApiResponse<StripePricesResponse>>('/stripe/prices');
    if (!response.data.data.publishableKey || !response.data.data.tiers) {
      throw apiFailure(response.data.data, 'Failed to get prices');
    }
    const data = {
      publishableKey: response.data.data.publishableKey,
      tiers: response.data.data.tiers,
    };
    pricesCache = { data, fetchedAt: now };
    return data;
  },

  createCheckoutSession: async (
    orgId: string,
    priceId: string
  ): Promise<{ sessionId: string; url: string }> => {
    const response = await apiClient.post<ApiResponse<CheckoutSessionResponse>>(
      `/organizations/${orgId}/checkout`,
      { priceId }
    );
    if (!response.data.data.success || !response.data.data.sessionId || !response.data.data.url) {
      throw apiFailure(response.data.data, 'Failed to create checkout session');
    }
    return {
      sessionId: response.data.data.sessionId,
      url: response.data.data.url,
    };
  },

  createPortalSession: async (
    orgId: string,
    options?: { flow?: 'payment_method_update' }
  ): Promise<{ url: string }> => {
    const response = await apiClient.post<ApiResponse<PortalSessionResponse>>(
      `/organizations/${orgId}/portal`,
      options?.flow ? { flow: options.flow } : {}
    );
    if (!response.data.data.success || !response.data.data.url) {
      throw apiFailure(response.data.data, 'Failed to create portal session');
    }
    return { url: response.data.data.url };
  },

  changePlan: async (
    orgId: string,
    priceId: string,
    couponCode?: string
  ): Promise<{ success: boolean; redirectUrl?: string }> => {
    const response = await apiClient.post<ApiResponse<ChangePlanResponse>>(
      `/organizations/${orgId}/change-plan`,
      { priceId, ...(couponCode ? { couponCode } : {}) }
    );
    const payload = response.data.data;
    if (!payload.success) {
      if (payload.errorCode === 'DOWNGRADE_BLOCKED' && payload.impact) {
        throw new DowngradeBlockedError(
          payload.error || 'Downgrade blocked by usage overage',
          payload.impact
        );
      }
      if (payload.errorCode === 'INVOICE_PAYMENT_REQUIRED' && payload.invoiceUrl) {
        throw new InvoicePaymentRequiredError(
          payload.error || 'Please complete the invoice payment to activate your new plan.',
          payload.invoiceUrl
        );
      }
      if (payload.errorCode === 'PORTAL_REQUIRED') {
        const reason: PortalRequiredReason =
          payload.errorReason === '3ds_required' ||
          payload.errorReason === 'card_declined' ||
          payload.errorReason === 'incomplete'
            ? payload.errorReason
            : 'incomplete';
        throw new PortalRequiredError(
          payload.error || 'Please complete this change via the billing portal.',
          reason
        );
      }
      throw apiFailure(payload, 'Failed to change plan');
    }
    return { success: true, redirectUrl: payload.redirectUrl };
  },

  previewPlanChange: async (
    orgId: string,
    priceId: string,
    couponCode?: string
  ): Promise<PlanChangePreview> => {
    const response = await apiClient.post<ApiResponse<PreviewPlanChangeResponse>>(
      `/organizations/${orgId}/preview-plan-change`,
      { priceId, ...(couponCode ? { couponCode } : {}) }
    );
    if (!response.data.data.success || !response.data.data.preview) {
      throw apiFailure(response.data.data, 'Failed to preview plan change');
    }
    return response.data.data.preview;
  },

  cancelSubscription: async (orgId: string): Promise<{ currentPeriodEnd?: string }> => {
    const response = await apiClient.post<ApiResponse<CancelSubscriptionResponse>>(
      `/organizations/${orgId}/cancel-subscription`
    );
    const payload = response.data.data;
    if (!payload.success) {
      if (payload.errorCode === 'DOWNGRADE_BLOCKED' && payload.impact) {
        throw new DowngradeBlockedError(
          payload.error || 'Cancellation blocked by usage overage',
          payload.impact
        );
      }
      throw apiFailure(payload, 'Failed to cancel subscription');
    }
    return { currentPeriodEnd: payload.currentPeriodEnd };
  },

  getDowngradeImpact: async (
    orgId: string,
    targetTier: 'free' | 'starter' | 'pro' | 'business'
  ): Promise<DowngradeImpact> => {
    const response = await apiClient.post<
      ApiResponse<{ success: boolean; impact?: DowngradeImpact; error?: string }>
    >(`/organizations/${orgId}/downgrade-impact`, { targetTier });
    const payload = response.data.data;
    if (!payload.success || !payload.impact) {
      throw apiFailure(payload, 'Failed to compute downgrade impact');
    }
    return payload.impact;
  },

  resumeSubscription: async (orgId: string): Promise<{ success: boolean }> => {
    const response = await apiClient.post<ApiResponse<ResumeSubscriptionResponse>>(
      `/organizations/${orgId}/resume-subscription`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to resume subscription');
    }
    return { success: true };
  },

  /**
   * Cancel a scheduled downgrade — release the Stripe schedule so the sub
   * continues renewing on the current tier. Distinct from resumeSubscription
   * which reverses a cancel_at_period_end cancellation.
   */
  cancelPendingDowngrade: async (orgId: string): Promise<{ success: boolean }> => {
    const response = await apiClient.post<ApiResponse<ResumeSubscriptionResponse>>(
      `/organizations/${orgId}/cancel-pending-downgrade`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to cancel scheduled downgrade');
    }
    return { success: true };
  },
};

export default subscriptionApi;
